import { motion } from 'motion/react';
import { cn } from '../../lib/cn.ts';
import { motionPresets } from '../../lib/motionPresets.ts';
import { formatInt } from '../../lib/format.ts';
import { WORK_STATE_META } from '../../lib/workState.ts';
import type { WorkState } from '../../lib/workState.ts';
import { LiveNumber } from '../live/LiveNumber.tsx';

export interface StageFlowStage {
  id: string;
  label: string;
  /** Items in this stage; the stage's number. */
  count: number;
  /** One mark per item, in its work state (queued hollow, running and verifying cyan, failed red). */
  marks: readonly WorkState[];
  /** One short fact beside the number: "9m 03s", "next in 2m". */
  meta?: string;
  /** Something is moving here now: its track is drawn in the working colour. */
  active?: boolean;
}

export interface StageFlowProps {
  stages: readonly StageFlowStage[];
  /** Accessible name: "Stages of the landing queue". */
  label: string;
  /** Marks drawn per stage before "+N". */
  maxMarks?: number;
  className?: string;
}

const MARK: Record<WorkState, string> = {
  'needs-you': 'bg-status-attention-fill',
  failed: 'bg-status-danger-fill',
  running: 'bg-status-working-fill',
  verifying: 'bg-status-working-fill',
  'waiting-for-cpu': 'border border-fg-subtle',
  queued: 'border border-fg-subtle',
  landed: 'bg-fg-subtle',
  stale: 'bg-border-strong',
};

/** "4 verifying, 1 failed" for a stage's accessible name. */
export function stageSummary(s: Pick<StageFlowStage, 'label' | 'count' | 'marks'>): string {
  if (!s.count) return `${s.label}, empty`;
  const by = new Map<WorkState, number>();
  for (const m of s.marks) by.set(m, (by.get(m) ?? 0) + 1);
  const parts = [...by].map(([state, n]) => `${formatInt(n)} ${WORK_STATE_META[state].label.toLowerCase()}`);
  return `${s.label}, ${formatInt(s.count)}${parts.length ? `: ${parts.join(', ')}` : ''}`;
}

/**
 * A pipeline's stages as one flow, left to right (pipelines.md 4.3.1): each stage a track that
 * fills once from the left, its live count, one fact, and a mark per item in its work state. Stages
 * with work share the width; an empty stage keeps only its name and a quiet zero, so an empty
 * Deploying never takes a full column. Nothing loops: the tracks draw once, marks arrive once.
 */
export function StageFlow({ stages, label, maxMarks = 24, className }: StageFlowProps) {
  return (
    <ol aria-label={label} className={cn('flex min-w-0 items-stretch gap-3', className)}>
      {stages.map((s, i) => {
        const empty = s.count === 0;
        const shown = s.marks.slice(0, maxMarks);
        const more = s.marks.length - shown.length;
        return (
          <li key={s.id} aria-label={stageSummary(s)} data-stage={s.id} className={cn('flex min-w-0 flex-col gap-2', empty ? 'w-20 shrink-0 max-sm:hidden' : 'flex-1 basis-0')}>
            {empty ? (
              <span className="mt-0.5 block border-t border-dashed border-border-strong" aria-hidden />
            ) : (
              <span className="block h-1 overflow-hidden rounded-pill bg-active" aria-hidden>
                <motion.span
                  className={cn('block h-full origin-left rounded-pill', s.active ? 'bg-status-working-fill' : 'bg-fg-subtle')}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ ...motionPresets.move, delay: i * 0.06 }}
                />
              </span>
            )}
            <span className="flex min-w-0 items-baseline gap-2">
              <span className={cn('truncate text-caption font-medium', empty ? 'text-fg-subtle' : 'text-fg-muted')}>{s.label}</span>
            </span>
            <span className="flex min-w-0 items-baseline gap-2">
              <LiveNumber value={s.count} className={cn('text-title font-semibold', empty ? 'text-fg-subtle' : 'text-fg')} />
              {s.meta && !empty && <span className="min-w-0 truncate text-caption text-fg-subtle num">{s.meta}</span>}
            </span>
            {!empty && (
              <span className="flex min-w-0 flex-wrap items-center gap-1" aria-hidden>
                {shown.map((m, n) => (
                  <span key={n} data-mark={m} className={cn('motion-arrive size-2 shrink-0 rounded-pill', MARK[m])} />
                ))}
                {more > 0 && <span className="text-caption text-fg-subtle num">+{formatInt(more)}</span>}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
